import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { emailNotificationApi } from "../../../api/emailNotificationApi";

const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: theme.spacing(3, 0, 5),
}));

const StepContent = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const FormationsList = styled("div")(({ theme }) => ({
  maxHeight: 200,
  overflow: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
}));

const PreviewBox = styled("div")(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  maxHeight: 300,
  overflow: "auto",
}));

const ActionsContainer = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(0, 2),
}));

const steps = [
  "Choisir le template",
  "Vérifier les formations",
  "Prévisualiser et envoyer",
];

const EmailSendDialog = ({ open, onClose, apprenant, formations }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Charger les templates au montage
  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await emailNotificationApi.getAllTemplates(accessToken);

      if (response.status === 200) {
        // Filtrer seulement les templates de type "apprenant"
        const apprenantTemplates = response.data.filter(
          (template) => template.type === "apprenant"
        );
        setTemplates(apprenantTemplates);
      } else {
        console.error("Erreur lors du chargement des templates");
        alert("Erreur lors du chargement des templates");
      }
    } catch (error) {
      console.error("Erreur lors du chargement des templates:", error);
      alert("Erreur lors du chargement des templates");
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedTemplate) {
      alert("Veuillez sélectionner un template");
      return;
    }

    if (activeStep === 1) {
      // Étape de prévisualisation
      generatePreview();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedTemplate("");
    setPreviewData(null);
    onClose();
  };

  const generatePreview = async () => {
    if (!selectedTemplate) return;

    setPreviewLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await emailNotificationApi.getTemplateById(
        selectedTemplate,
        accessToken
      );

      if (response.status === 200) {
        const template = response.data;

        // Simuler les données de l'apprenant pour la prévisualisation
        const previewData = {
          subject: template.subject,
          content: template.content,
          // Remplacer les variables par des exemples
          variables: {
            civilité: apprenant.civility || "M.",
            nom: apprenant.lastname || "Dupont",
            prénom: apprenant.firstname || "Jean",
            email: apprenant.email || "apprenant@example.com",
            téléphone: apprenant.phone || "01 23 45 67 89",
            profession: apprenant.profession || "Médecin",
            spécialité: apprenant.specialite || "Cardiologie",
            adresse: apprenant.adress || "123 Rue de la Paix",
            "code postal": apprenant.zip || "75001",
            ville: apprenant.city || "Paris",
            "liste des formations": generateFormationsList(),
          },
        };

        // Remplacer les variables dans le contenu
        let previewContent = template.content;
        let previewSubject = template.subject;

        Object.entries(previewData.variables).forEach(([key, value]) => {
          const regex = new RegExp(`{${key}}`, "g");
          previewContent = previewContent.replace(regex, value);
          previewSubject = previewSubject.replace(regex, value);
        });

        setPreviewData({
          ...previewData,
          finalSubject: previewSubject,
          finalContent: previewContent,
        });

        setActiveStep(2);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la génération de la prévisualisation:",
        error
      );
      alert("Erreur lors de la génération de la prévisualisation");
    } finally {
      setPreviewLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR");
  };

  const generateFormationsList = () => {
    if (!formations || formations.length === 0) {
      return "<p>Aucune formation</p>";
    }

    const formationsList = formations
      .map(
        (formation) =>
          `<div style="margin-bottom: 10px; padding: 10px; border: 1px solid #ddd; border-radius: 5px;">
        <strong>${formation.name}</strong> (${
            formation.programNumber ?? ""
          })<br/>
        Du ${formatDate(formation.start_date)} au ${formatDate(
            formation.end_date
          )}
      </div>`
      )
      .join("");

    return `<div style="margin: 10px 0;">${formationsList}</div>`;
  };

  const handleSendEmail = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await emailNotificationApi.sendEmailToApprenant(
        selectedTemplate,
        apprenant.id,
        accessToken
      );

      if (response.status === 200) {
        alert("Email envoyé avec succès !");
        handleClose();
      } else {
        alert(
          response.data?.message || response.message || "Erreur lors de l'envoi"
        );
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email:", error);
      alert("Erreur lors de l'envoi de l'email");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepContent>
            <Typography variant="h6" gutterBottom>
              Sélectionnez un template d'email
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Choisissez le template que vous souhaitez utiliser pour envoyer un
              email à {apprenant.firstname} {apprenant.lastname}.
            </Typography>

            {templatesLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <FormControl fullWidth variant="outlined">
                <InputLabel>Template d'email</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  label="Template d'email"
                >
                  {templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedTemplate && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Template sélectionné :
                </Typography>
                {templates.find((t) => t.id === selectedTemplate) && (
                  <Typography variant="body2" color="textSecondary">
                    {templates.find((t) => t.id === selectedTemplate).subject}
                  </Typography>
                )}
              </Box>
            )}
          </StepContent>
        );

      case 1:
        return (
          <StepContent>
            <Typography variant="h6" gutterBottom>
              Vérification des formations
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Voici les formations qui seront incluses dans l'email (si le
              template contient la balise "liste des formations") :
            </Typography>

            <FormationsList>
              {formations && formations.length > 0 ? (
                <List dense>
                  {formations.map((formation, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{formation.name}</strong> (
                            {formation.programNumber})
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="textSecondary">
                            Du {formatDate(formation.start_date)} au{" "}
                            {formatDate(formation.end_date)}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Aucune formation trouvée pour cet apprenant.
                </Typography>
              )}
            </FormationsList>

            <Box mt={2}>
              <Alert severity="info">
                <Typography variant="body2">
                  Cliquez sur "Suivant" pour prévisualiser l'email avant
                  l'envoi.
                </Typography>
              </Alert>
            </Box>
          </StepContent>
        );

      case 2:
        return (
          <StepContent>
            <Typography variant="h6" gutterBottom>
              Prévisualisation et envoi
            </Typography>

            {previewLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : previewData ? (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Destinataire : {apprenant.firstname} {apprenant.lastname} (
                  {apprenant.email})
                </Typography>

                <Divider style={{ margin: "16px 0" }} />

                <Typography variant="subtitle2" gutterBottom>
                  Sujet :
                </Typography>
                <Typography
                  variant="body2"
                  paragraph
                  style={{ fontStyle: "italic" }}
                >
                  {previewData.finalSubject}
                </Typography>

                <Typography variant="subtitle2" gutterBottom>
                  Contenu :
                </Typography>
                <PreviewBox
                  dangerouslySetInnerHTML={{ __html: previewData.finalContent }}
                />

                <Box mt={2}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      Vérifiez le contenu avant l'envoi. L'email sera envoyé
                      immédiatement.
                    </Typography>
                  </Alert>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Erreur lors de la génération de la prévisualisation.
              </Typography>
            )}
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Envoyer un email à {apprenant?.firstname} {apprenant?.lastname}
      </DialogTitle>

      <DialogContent>
        <StyledStepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </StyledStepper>

        {renderStepContent(activeStep)}
      </DialogContent>

      <ActionsContainer>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>

        <Button
          Button
          onClick={handleBack}
          disabled={loading || activeStep === 0}
        >
          Retour
        </Button>

        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSendEmail}
            disabled={loading || !previewData}
          >
            {loading ? <CircularProgress size={20} /> : "Envoyer l'email"}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={loading || (activeStep === 0 && !selectedTemplate)}
          >
            {activeStep === 1 && previewLoading ? (
              <CircularProgress size={20} />
            ) : (
              "Suivant"
            )}
          </Button>
        )}
      </ActionsContainer>
    </Dialog>
  );
};

EmailSendDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  apprenant: PropTypes.object.isRequired,
  formations: PropTypes.array.isRequired,
};

export default EmailSendDialog;
